import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Auto Lease Calculator — specific to vehicles
export default function AutoLeaseCalc() {
  const [msrp, setMsrp] = useState(45000);
  const [negotiated, setNegotiated] = useState(43000);
  const [residualPct, setResidualPct] = useState(55);
  const [term, setTerm] = useState(36);
  const [rate, setRate] = useState(5);
  const [salesTax, setSalesTax] = useState(6);
  const [tradeIn, setTradeIn] = useState(0);
  const [down, setDown] = useState(2000);
  const [fees, setFees] = useState(1000);
  const c = useMemo(() => {
    const residual = msrp * residualPct/100;
    const capCost = negotiated + fees - tradeIn - down;
    const moneyFactor = rate/2400;
    const depreciation = (capCost - residual) / term;
    const finance = (capCost + residual) * moneyFactor;
    const basePayment = depreciation + finance;
    const tax = basePayment * salesTax/100;
    const monthly = basePayment + tax;
    const totalOutOfPocket = down + fees + monthly * term;
    return { monthly, residual, capCost, depreciation, finance, tax, totalOutOfPocket, moneyFactor };
  }, [msrp, negotiated, residualPct, term, rate, salesTax, tradeIn, down, fees]);
  return (
    <CalcShell look="cnet" title="Auto Lease Calculator" subtitle="Estimate car lease monthly payment including residual, money factor, sales tax, and fees."
      about={<><p>Vehicle leasing pays for the vehicle's <b>depreciation</b> (Cap cost − Residual) plus a finance charge (Money factor × (Cap cost + Residual)). Sales tax is typically applied to the monthly payment.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Vehicle MSRP</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={msrp} onChange={e=>setMsrp(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Negotiated sale price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={negotiated} onChange={e=>setNegotiated(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Residual value (% of MSRP)</label><div className="flex gap-2"><input type="number" step="0.01" value={residualPct} onChange={e=>setResidualPct(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Lease term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">months</span></div></div>
          <div className="field"><label>Interest rate (APR)</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Sales tax</label><div className="flex gap-2"><input type="number" step="0.01" value={salesTax} onChange={e=>setSalesTax(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Trade-in value</label><div className="flex gap-2"><span>$</span><input type="number" value={tradeIn} onChange={e=>setTradeIn(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Down payment</label><div className="flex gap-2"><span>$</span><input type="number" value={down} onChange={e=>setDown(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Fees & other charges</label><div className="flex gap-2"><span>$</span><input type="number" value={fees} onChange={e=>setFees(+e.target.value||0)} className="flex-1"/></div></div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Monthly lease payment</div><div className="result-value" data-testid="alease-monthly">{fmt(c.monthly)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Money factor</td><td className="text-right">{c.moneyFactor.toFixed(5)}</td></tr>
            <tr><td>Adjusted capitalized cost</td><td className="text-right">{fmt(c.capCost)}</td></tr>
            <tr><td>Residual value</td><td className="text-right">{fmt(c.residual)}</td></tr>
            <tr><td>Monthly depreciation</td><td className="text-right">{fmt(c.depreciation)}</td></tr>
            <tr><td>Monthly finance charge</td><td className="text-right">{fmt(c.finance)}</td></tr>
            <tr><td>Monthly tax</td><td className="text-right">{fmt(c.tax)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Total out-of-pocket</td><td className="text-right text-blue-700">{fmt(c.totalOutOfPocket)}</td></tr>
          </tbody></table>
        </div>
      </div>
      <hr className="my-8 border-slate-200"/>
      <AutoLeaseTotalCost/>
    </CalcShell>
  );
}

function AutoLeaseTotalCost() {
  const [target, setTarget] = useState(400);
  const [term, setTerm] = useState(36);
  const [residualPct, setResidualPct] = useState(55);
  const [msrp, setMsrp] = useState(45000);
  const [rate, setRate] = useState(5);
  const [salesTax, setSalesTax] = useState(6);
  const c = useMemo(() => {
    // Solve for negotiated price s.t. monthly = target
    // depreciation = (capCost - residual) / term; finance = (capCost + residual) * mf; base = dep + fin; monthly = base*(1+tax/100)
    // capCost ≈ negotiated
    const residual = msrp * residualPct/100;
    const mf = rate/2400;
    const taxFactor = 1 + salesTax/100;
    const baseNeeded = target / taxFactor;
    // baseNeeded = (cap - residual)/term + (cap + residual)*mf
    //            = cap*(1/term + mf) + residual*(mf - 1/term)
    // cap = (baseNeeded - residual*(mf - 1/term)) / (1/term + mf)
    const cap = (baseNeeded - residual*(mf - 1/term)) / (1/term + mf);
    const totalPaid = target * term;
    const totalLeaseCost = totalPaid + Math.max(0, msrp - cap);
    return { cap, totalPaid, totalLeaseCost, residual, mf };
  }, [target, term, residualPct, msrp, rate, salesTax]);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">Total Lease Cost Sub-Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Enter a target monthly payment and see the maximum negotiated sale price that fits.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Target monthly payment</label><div className="flex gap-2"><span>$</span><input type="number" value={target} onChange={e=>setTarget(+e.target.value||0)} className="flex-1" data-testid="alease-target"/></div></div>
          <div className="field"><label>Vehicle MSRP</label><div className="flex gap-2"><span>$</span><input type="number" value={msrp} onChange={e=>setMsrp(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Residual (% MSRP)</label><div className="flex gap-2"><input type="number" value={residualPct} onChange={e=>setResidualPct(+e.target.value||0)} className="flex-1"/><span>%</span></div></div>
          <div className="field"><label>Term</label><div className="flex gap-2"><input type="number" value={term} onChange={e=>setTerm(+e.target.value||0)} className="flex-1"/><span>months</span></div></div>
          <div className="field"><label>APR</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span>%</span></div></div>
          <div className="field"><label>Sales tax</label><div className="flex gap-2"><input type="number" step="0.01" value={salesTax} onChange={e=>setSalesTax(+e.target.value||0)} className="flex-1"/><span>%</span></div></div>
        </div>
        <div>
          <div className="result-panel mb-3"><div className="result-title">Max negotiated price to hit target</div><div className="result-value" data-testid="alease-max-price">{fmt(c.cap)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>Total of {term} lease payments</td><td className="text-right">{fmt(c.totalPaid)}</td></tr>
            <tr><td>Residual (kept by lessor)</td><td className="text-right">{fmt(c.residual)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Total lease cost (payments + depreciation captured)</td><td className="text-right">{fmt(c.totalLeaseCost)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </div>
  );
}
